import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, Building2, User, ChevronRight, Check } from 'lucide-react';
import api from '../../../services/api';

const websiteTypeIcons = {
  portfolio: Briefcase,
  business: Building2,
  freelancer: User,
};

const WebsiteTypeSelection = () => {
  const navigate = useNavigate();
  const [websiteTypes, setWebsiteTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchWebsiteTypes();
  }, []);

  const fetchWebsiteTypes = async () => {
    try {
      const response = await api.get('/templates/website-types/');
      setWebsiteTypes(response.data.website_types || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching website types:', err);
      setError('Failed to load website types');
      setLoading(false);
    }
  };

  const handleSelectType = async () => {
    if (!selectedType) return;

    setSaving(true);
    try {
      await api.post('/templates/user/set-website-type/', {
        website_type: selectedType
      });

      // Route based on website type
      if (selectedType === 'portfolio') {
        // Portfolio: Upload CV/Resume
        navigate('/dashboard/onboarding/cv-upload');
      } else {
        // Business/Freelancer: Upload content document
        navigate('/dashboard/onboarding/content-upload');
      }
    } catch (err) {
      console.error('Error setting website type:', err);
      setError('Failed to save website type');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            What type of website do you want to create?
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Choose the type that best fits your needs. This will customize your dashboard with relevant sections.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg text-red-400 text-center">
            {error}
          </div>
        )}

        {/* Website Type Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {websiteTypes.map((type) => {
            const Icon = websiteTypeIcons[type.key] || Briefcase;
            const isSelected = selectedType === type.key;

            return (
              <div
                key={type.key}
                onClick={() => setSelectedType(type.key)}
                className={`relative cursor-pointer rounded-2xl p-6 transition-all duration-300 transform hover:scale-[1.02] ${
                  isSelected
                    ? 'bg-gradient-to-br from-cyan-600 to-blue-700 shadow-xl shadow-cyan-500/20'
                    : 'bg-gray-800/80 hover:bg-gray-800 border border-gray-700'
                }`}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-white rounded-full flex items-center justify-center">
                    <Check size={16} className="text-cyan-600" />
                  </div>
                )}

                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${
                  isSelected ? 'bg-white/20' : 'bg-cyan-500/10'
                }`}>
                  <Icon size={28} className={isSelected ? 'text-white' : 'text-cyan-400'} />
                </div>

                {/* Content */}
                <h3 className={`text-xl font-bold mb-2 ${isSelected ? 'text-white' : 'text-white'}`}>
                  {type.name}
                </h3>
                <p className={`text-sm mb-4 ${isSelected ? 'text-white/80' : 'text-gray-400'}`}>
                  {type.description}
                </p>

                {/* Sample sections */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className={`text-xs font-medium mb-2 ${isSelected ? 'text-white/60' : 'text-gray-500'}`}>
                    INCLUDES SECTIONS:
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {type.default_sections?.slice(0, 5).map((section) => (
                      <span
                        key={section}
                        className={`text-xs px-2 py-1 rounded-full ${
                          isSelected
                            ? 'bg-white/20 text-white'
                            : 'bg-gray-700 text-gray-300'
                        }`}
                      >
                        {section.replace('_', ' ')}
                      </span>
                    ))}
                    {type.default_sections?.length > 5 && (
                      <span className={`text-xs px-2 py-1 ${isSelected ? 'text-white/60' : 'text-gray-500'}`}>
                        +{type.default_sections.length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Continue Button */}
        <div className="flex justify-center">
          <button
            onClick={handleSelectType}
            disabled={!selectedType || saving}
            className={`flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
              selectedType && !saving
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg hover:shadow-cyan-500/30 transform hover:scale-[1.02]'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Continue
                <ChevronRight size={20} />
              </>
            )}
          </button>
        </div>

        {/* Skip option */}
        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/dashboard/onboarding/cv-upload')}
            className="text-gray-500 hover:text-gray-400 text-sm"
          >
            Skip for now (defaults to Portfolio)
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebsiteTypeSelection;
